import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';

// Survives React StrictMode remounts — only one popup can be open at a time across all posts
const _openPopup = { postId: null, type: null };

const PRIVACY_OPTIONS = [
    {
        value: 0,
        label: 'Public',
        icon: '🌍',
        desc: 'Everyone can see this post',
    },
    {
        value: 1,
        label: 'Only Me',
        icon: '🔒',
        desc: 'Only you can see this post',
    },
    {
        value: 2,
        label: 'Friends Only',
        icon: '👥',
        desc: 'Only your friends can see this post',
    },
];

const toLocalDatetimeInput = (utcStr) => {
    if (!utcStr) return '';
    const ts = utcStr.includes('Z') || utcStr.includes('+') ? utcStr : utcStr.replace(' ', 'T') + 'Z';
    const d = new Date(ts);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const FavoriteButton = ({ post, auth, GetPoost, allPostIds, onPrivacyChange, onDateChange, isTagged, onRemoveTag }) => {
    const [favAnimating, setFavAnimating] = useState(false);
    const [favorited, setFavorited] = useState(post.is_fav);
    const [favId, setFavId] = useState(post.is_fav ? post.is_fav.id : null);
    const postId = post.post_data.id;

    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [showPrivacyPopup, _setShowPrivacyPopup] = useState(
        () => _openPopup.postId === postId && _openPopup.type === 'privacy'
    );
    const [privacy, setPrivacy] = useState(post.post_data.privacy ?? 0);
    const [privacyLoading, setPrivacyLoading] = useState(false);
    const [showDatePopup, _setShowDatePopup] = useState(
        () => _openPopup.postId === postId && _openPopup.type === 'date'
    );
    const [dateOnly, setDateOnly] = useState('');
    const [timeOnly, setTimeOnly] = useState('');
    const [dateLoading, setDateLoading] = useState(false);
    const [showEditPopup, _setShowEditPopup] = useState(
        () => _openPopup.postId === postId && _openPopup.type === 'edit'
    );
    const [editContent, setEditContent] = useState('');
    const [editLoading, setEditLoading] = useState(false);
    const [editFile, setEditFile] = useState(null);
    const [editFilePreviewUrl, setEditFilePreviewUrl] = useState(null);
    const [editRemoveFile, setEditRemoveFile] = useState(false);

    const setShowPrivacyPopup = (val) => {
        _setShowPrivacyPopup(val);
        if (val) { _openPopup.postId = postId; _openPopup.type = 'privacy'; }
        else if (_openPopup.postId === postId && _openPopup.type === 'privacy') { _openPopup.postId = null; _openPopup.type = null; }
    };
    const setShowDatePopup = (val) => {
        _setShowDatePopup(val);
        if (val) { _openPopup.postId = postId; _openPopup.type = 'date'; }
        else if (_openPopup.postId === postId && _openPopup.type === 'date') { _openPopup.postId = null; _openPopup.type = null; }
    };
    const setShowEditPopup = (val) => {
        _setShowEditPopup(val);
        if (val) { _openPopup.postId = postId; _openPopup.type = 'edit'; }
        else if (_openPopup.postId === postId && _openPopup.type === 'edit') { _openPopup.postId = null; _openPopup.type = null; }
    };

    const dropdownRef = useRef(null);
    const fileInputRef = useRef(null);
    const anyPopupOpen = useRef(false);
    const isMyPost = auth.id === post.post_data.user.id;

    useEffect(() => {
        return () => {
            if (_openPopup.postId === postId) { _openPopup.postId = null; _openPopup.type = null; }
        };
    }, []);

    useEffect(() => {
        if (!editFile) { setEditFilePreviewUrl(null); return; }
        const url = URL.createObjectURL(editFile);
        setEditFilePreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [editFile]);

    const handleFavorite = async () => {
        setFavAnimating(true);
        try {
            if (favorited) {
                if (!favId) return;
                const response = await axios.post('https://buzzinguniverse.com/backend/api/disfav-request', {
                    id: favId,
                    post_id: post.post_data.id,
                    user_id: auth.id
                });
                if (response.data.code === 200) {
                    setFavorited(false);
                    setFavId(null);
                }
            } else {
                const response = await axios.post('https://buzzinguniverse.com/backend/api/fav-request', {
                    post_id: post.post_data.id,
                    user_id: auth.id
                });
                if (response.data.code === 200) {
                    setFavorited(true);
                    setFavId(response.data.data.id);
                }
            }
        } catch (error) {
            console.error("Error favoriting/unfavoriting post:", error);
            alert(error.response?.data?.message || "Failed to process request");
        } finally {
            setTimeout(() => setFavAnimating(false), 500);
            GetPoost && GetPoost();
        }
    };

    const handleDelete = async (e) => {
        try {
            const container = e.target.closest('.personal_des_box');
            const idsToDelete = allPostIds && allPostIds.length > 0 ? allPostIds : [post.post_data.id];
            const confirmDelete = window.confirm("Are you sure you want to delete this post?");
            if (!confirmDelete) return;

            const deletePromises = idsToDelete.map(id =>
                axios.post('https://buzzinguniverse.com/backend/api/delete/post', {
                    post_id: id,
                    user_id: auth.id
                })
            );

            const results = await Promise.all(deletePromises);
            const allSucceeded = results.every(res => res.data.code === 200);

            if (allSucceeded) {
                // Hide instead of remove(): this node is owned by React, and removing it
                // makes the next re-render throw (removeChild/insertBefore) and blank the page
                if (container) container.style.display = 'none';
                GetPoost && GetPoost();
            } else {
                alert("Some images failed to delete");
            }
        } catch (error) {
            console.error("Error deleting post:", error);
            alert(error.response?.data?.message || "Failed to delete post");
        }
    };

    const openDatePopup = () => {
        const dt = toLocalDatetimeInput(post.post_data.created_at);
        const [d, t] = dt.split('T');
        setDateOnly(d || '');
        setTimeOnly(t || '00:00');
        setDropdownVisible(false);
        setShowDatePopup(true);
    };

    const openEditPopup = () => {
        const plain = (post.post_data.details || '').replace(/<[^>]*>/g, '');
        setEditContent(plain !== 'null' ? plain : '');
        setEditFile(null);
        setEditRemoveFile(false);
        setDropdownVisible(false);
        setShowEditPopup(true);
    };

    const handleEditSave = async () => {
        const hasMedia = (post.post_data.file && !editRemoveFile) || editFile;
        if (!editContent.trim() && !hasMedia) return;
        setEditLoading(true);
        try {
            const formData = new FormData();
            formData.append('user_id', auth.id);
            formData.append('post_id', post.post_data.id);
            formData.append('details', editContent);
            if (editFile) {
                formData.append('file', editFile);
            } else if (editRemoveFile) {
                formData.append('remove_file', '1');
            }
            const res = await axios.post('https://buzzinguniverse.com/backend/api/edit/post', formData);
            if (res.data.code === 200) {
                post.post_data.details = editContent;
                if (res.data.data?.file !== undefined) {
                    post.post_data.file = res.data.data.file;
                    post.post_data.file_type = res.data.data.file_type;
                }
                setShowEditPopup(false);
                GetPoost && GetPoost();
            }
        } catch (error) {
            console.error('Edit post failed:', error);
        } finally {
            setEditLoading(false);
        }
    };

    const updateDate = async () => {
        if (!dateOnly || !timeOnly) return;
        setDateLoading(true);
        try {
            const utcDate = new Date(`${dateOnly}T${timeOnly}`).toISOString().slice(0, 19).replace('T', ' ');
            const res = await axios.post('https://buzzinguniverse.com/backend/api/update-post-date', {
                user_id: auth.id,
                post_id: post.post_data.id,
                created_at: utcDate,
            });
            if (res.data.code === 200) {
                onDateChange && onDateChange(post.post_data.id, utcDate);
                setShowDatePopup(false);
                GetPoost && GetPoost();
            }
        } catch (error) {
            console.error('Date update failed:', error);
        } finally {
            setDateLoading(false);
        }
    };

    const updatePrivacy = async (newPrivacy) => {
        setPrivacyLoading(true);
        try {
            await axios.post('https://buzzinguniverse.com/backend/api/update-post-privacy', {
                user_id: auth.id,
                post_id: post.post_data.id,
                privacy: newPrivacy,
            });
            setPrivacy(newPrivacy);
            onPrivacyChange && onPrivacyChange(newPrivacy);
            setShowPrivacyPopup(false);
        } catch (error) {
            console.error('Privacy update failed:', error);
        } finally {
            setPrivacyLoading(false);
        }
    };

    // anyPopupOpen ref sync karo — jab bhi privacy ya date popup open ho
    useEffect(() => {
        anyPopupOpen.current = showPrivacyPopup || showDatePopup || showEditPopup;
    }, [showPrivacyPopup, showDatePopup, showEditPopup]);

    const handleOutsideClick = useCallback((e) => {
        // Popup open hai toh mousedown bilkul ignore karo
        if (anyPopupOpen.current) return;
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
            setDropdownVisible(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [handleOutsideClick]);

    return (
        <>
            <div className="fav-btn-dropdown" ref={dropdownRef}>
                <button
                    className="fav-btn-dropdown-toggle"
                    onClick={() => setDropdownVisible(!dropdownVisible)}
                >
                    <i className="fas fa-ellipsis-v"></i>
                </button>

                {dropdownVisible && (
                    <div className="fav-btn-dropdown-menu">
                        <button
                            onClick={() => { handleFavorite(); setDropdownVisible(false); }}
                            className="fav-btn-dropdown-item"
                        >
                            <i className={`fas fa-star me-2 ${favorited ? 'text-warning' : ''}`}></i>
                            {favorited ? 'Unmark as Favorite' : 'Mark as Favorite'}
                        </button>
                        {isMyPost && (
                            <button
                                onClick={() => { setDropdownVisible(false); setShowPrivacyPopup(true); }}
                                className="fav-btn-dropdown-item"
                            >
                                <i className="fas fa-lock me-2"></i>
                                Privacy
                                <span className="privacy-current-badge">
                                    {PRIVACY_OPTIONS.find(o => o.value === privacy)?.icon}
                                </span>
                            </button>
                        )}
                        {isMyPost && (
                            <button
                                onClick={openEditPopup}
                                className="fav-btn-dropdown-item"
                            >
                                <i className="fas fa-edit me-2"></i>
                                Edit Post
                            </button>
                        )}
                        {isMyPost && (
                            <button
                                onClick={openDatePopup}
                                className="fav-btn-dropdown-item"
                            >
                                <i className="fas fa-calendar-alt me-2"></i>
                                Edit Date
                            </button>
                        )}
                        {isTagged && (
                            <button
                                onClick={() => { setDropdownVisible(false); onRemoveTag && onRemoveTag(); }}
                                className="fav-btn-dropdown-item fav-btn-dropdown-item--danger"
                            >
                                <i className="fas fa-tag me-2"></i>
                                Remove Tag
                            </button>
                        )}
                        {isMyPost && (
                            <button
                                onClick={(e) => { handleDelete(e); setDropdownVisible(false); }}
                                className="fav-btn-dropdown-item fav-btn-dropdown-item--danger"
                            >
                                <i className="fas fa-trash me-2"></i>
                                Delete
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Privacy Popup */}
            {showPrivacyPopup && createPortal(
                <div className="privacy-popup-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowPrivacyPopup(false); }}>
                    <div className="privacy-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="privacy-popup-header">
                            <h3>Post Privacy</h3>
                            <button className="privacy-popup-close" onClick={() => setShowPrivacyPopup(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <p className="privacy-popup-subtitle">Who can see this post?</p>
                        <div className="privacy-options-list">
                            {PRIVACY_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`privacy-option-card ${privacy === opt.value ? 'active' : ''} ${privacyLoading ? 'disabled' : ''}`}
                                    onClick={() => !privacyLoading && updatePrivacy(opt.value)}
                                    disabled={privacyLoading}
                                >
                                    <span className="privacy-option-icon">{opt.icon}</span>
                                    <div className="privacy-option-text">
                                        <strong>{opt.label}</strong>
                                        <span>{opt.desc}</span>
                                    </div>
                                    {privacy === opt.value && (
                                        <span className="privacy-option-check">
                                            {privacyLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {showDatePopup && createPortal(
                <div className="privacy-popup-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowDatePopup(false); }}>
                    <div className="privacy-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="privacy-popup-header">
                            <h3>Edit Post Date</h3>
                            <button className="privacy-popup-close" onClick={() => setShowDatePopup(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <p className="privacy-popup-subtitle"></p>
                        <div style={{ padding: '16px 24px 24px' }}>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#6c757d', marginBottom: '6px', fontWeight: '500' }}>
                                    <i className="fas fa-calendar-alt me-1"></i> Date
                                </label>
                                <input
                                    type="date"
                                    value={dateOnly}
                                    onChange={(e) => setDateOnly(e.target.value)}
                                    max={new Date().toISOString().slice(0, 10)}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: '8px',
                                        border: '1px solid #dee2e6',
                                        fontSize: '15px',
                                    }}
                                />
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#6c757d', marginBottom: '6px', fontWeight: '500' }}>
                                    <i className="fas fa-clock me-1"></i> Time
                                </label>
                                <input
                                    type="time"
                                    value={timeOnly}
                                    onChange={(e) => setTimeOnly(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: '8px',
                                        border: '1px solid #dee2e6',
                                        fontSize: '15px',
                                    }}
                                />
                            </div>
                            <button
                                onClick={updateDate}
                                disabled={dateLoading || !dateOnly || !timeOnly}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    background: dateLoading ? '#aaa' : '#4a90e2',
                                    color: '#fff',
                                    border: 'none',
                                    fontSize: '15px',
                                    cursor: dateLoading ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {dateLoading ? <i className="fas fa-spinner fa-spin me-2"></i> : <i className="fas fa-check me-2"></i>}
                                Save
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {showEditPopup && createPortal(
                <div className="privacy-popup-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowEditPopup(false); }}>
                    <div className="privacy-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="privacy-popup-header">
                            <h3>Edit Post</h3>
                            <button className="privacy-popup-close" onClick={() => setShowEditPopup(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div style={{ padding: '16px 24px 24px' }}>
                            <textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                rows={4}
                                placeholder="What's on your mind?"
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6',
                                    fontSize: '15px',
                                    resize: 'vertical',
                                    fontFamily: 'inherit',
                                    lineHeight: '1.5',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    marginBottom: '12px',
                                }}
                            />
                            {/* Media section */}
                            {editFile ? (
                                // New file selected — show its preview
                                <div style={{ position: 'relative', marginBottom: '12px' }}>
                                    {editFile.type.startsWith('image/') && (
                                        <img src={editFilePreviewUrl} alt="new" style={{ width: '100%', maxHeight: '280px', objectFit: 'contain', borderRadius: '8px', background: '#f0f0f0', display: 'block' }} />
                                    )}
                                    {editFile.type.startsWith('video/') && (
                                        <video src={editFilePreviewUrl} controls style={{ width: '100%', borderRadius: '8px', maxHeight: '280px' }} />
                                    )}
                                    {editFile.type.startsWith('audio/') && (
                                        <audio src={editFilePreviewUrl} controls style={{ width: '100%' }} />
                                    )}
                                    {editFile.type === 'application/pdf' && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                                            <i className="fas fa-file-pdf" style={{ fontSize: '28px', color: '#e74c3c', flexShrink: 0 }} />
                                            <div>
                                                <div style={{ fontWeight: '600', fontSize: '14px' }}>{editFile.name}</div>
                                                <div style={{ fontSize: '12px', color: '#6c757d' }}>PDF Document</div>
                                            </div>
                                        </div>
                                    )}
                                    <button onClick={() => setEditFile(null)} title="Cancel new file" style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <i className="fas fa-times" style={{ fontSize: '12px' }} />
                                    </button>
                                </div>
                            ) : post.post_data.file && !editRemoveFile ? (
                                // Existing file — show preview with remove button
                                <div style={{ position: 'relative', marginBottom: '12px' }}>
                                    {['jpg','jpeg','png','gif','webp'].includes((post.post_data.file_type||'').toLowerCase()) && (
                                        <img src={`https://buzzinguniverse.com/backend/${post.post_data.file}`} alt="attached" style={{ width: '100%', maxHeight: '280px', objectFit: 'contain', borderRadius: '8px', background: '#f0f0f0', display: 'block' }} />
                                    )}
                                    {['mp4','mov','webm'].includes((post.post_data.file_type||'').toLowerCase()) && (
                                        <video src={`https://buzzinguniverse.com/backend/${post.post_data.file}`} controls style={{ width: '100%', borderRadius: '8px', maxHeight: '280px' }} />
                                    )}
                                    {['mp3','wav','ogg'].includes((post.post_data.file_type||'').toLowerCase()) && (
                                        <audio src={`https://buzzinguniverse.com/backend/${post.post_data.file}`} controls style={{ width: '100%' }} />
                                    )}
                                    {(post.post_data.file_type||'').toLowerCase() === 'pdf' && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                                            <i className="fas fa-file-pdf" style={{ fontSize: '28px', color: '#e74c3c', flexShrink: 0 }} />
                                            <div>
                                                <div style={{ fontWeight: '600', fontSize: '14px' }}>
                                                    {post.post_data.title && post.post_data.title !== 'posted an update' ? post.post_data.title : post.post_data.file.split('/').pop()}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#6c757d' }}>PDF Document</div>
                                            </div>
                                        </div>
                                    )}
                                    <button onClick={() => setEditRemoveFile(true)} title="Remove media" style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <i className="fas fa-times" style={{ fontSize: '12px' }} />
                                    </button>
                                </div>
                            ) : (
                                // No media / removed — show Add Media button
                                <div style={{ marginBottom: '12px' }}>
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px dashed #dee2e6', background: 'transparent', cursor: 'pointer', color: '#6c757d', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                    >
                                        <i className="fas fa-image" />
                                        Add Photo / Video / Audio / PDF
                                    </button>
                                </div>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/*,audio/*,.pdf"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) { setEditFile(f); setEditRemoveFile(false); }
                                    e.target.value = '';
                                }}
                            />
                            {(() => {
                                const hasMedia = (post.post_data.file && !editRemoveFile) || editFile;
                                const saveDisabled = editLoading || (!editContent.trim() && !hasMedia);
                                return (
                                    <button
                                        onClick={handleEditSave}
                                        disabled={saveDisabled}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            background: saveDisabled ? '#aaa' : '#4a90e2',
                                            color: '#fff',
                                            border: 'none',
                                            fontSize: '15px',
                                            cursor: saveDisabled ? 'not-allowed' : 'pointer',
                                            fontWeight: '500',
                                        }}
                                    >
                                        {editLoading ? <i className="fas fa-spinner fa-spin me-2"></i> : <i className="fas fa-check me-2"></i>}
                                        Save
                                    </button>
                                );
                            })()}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default FavoriteButton;
